import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SideDrawer from "./SideDrawer";
import { TbMenu2, TbSearch, TbBell } from "react-icons/tb";
import { FiLogOut, FiSettings, FiUser } from "react-icons/fi";
import AdminImage from "../images/admin.jpg";
import { useAuth } from "../contexts/AuthContext";

export const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const userName = user?.fullName || user?.username || "User";

  // Get page title based on current route
  const pageTitle = useMemo(() => {
    const path = location.pathname;
    const routeTitles = {
      '/': 'Dashboard',
      '/addnew': 'Add New Product',
      '/inventory': 'Inventory Management',
      '/orders': 'Orders',
      '/trackawbs': 'Track Shipments',
      '/auctions': 'Auctions',
      '/bids': 'Bids Management',
      '/bank-accounts': 'Bank Accounts',
      '/statistics': 'Statistics & Analytics',
      '/customers': 'Customer Management',
      '/activity': 'Activity Log',
      '/branches': 'Branch Locations',
      '/users': 'User Management',
      '/banners': 'Banner Management',
      '/system': 'System Settings',
      '/profile': 'Profile',
      '/settings': 'Settings'
    };

    // Check for dynamic routes (like /branches/:name or /product/:id)
    if (path.startsWith('/branches/') && path !== '/branches') {
      const branchName = path.split('/')[2];
      return `Branch: ${branchName.charAt(0).toUpperCase() + branchName.slice(1)}`;
    }
    if (path.startsWith('/product/')) {
      return 'Product Details';
    }
    if (path.startsWith('/order/')) {
      return 'Order Details';
    }

    return routeTitles[path] || 'GoldMart Admin';
  }, [location.pathname]);

  // Simulated notifications
  const notifications = [
    { id: 1, message: "New order received", time: "5 minutes ago" },
    { id: 2, message: "Product stock low", time: "1 hour ago" },
    { id: 3, message: "System update available", time: "2 hours ago" }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // TODO: Implement search functionality
      console.log("Searching for:", searchQuery);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 md:py-3 relative">
        <button
          className="p-2 hover:bg-gray-100 rounded-full transition-colors md:hidden"
          style={{ position: 'relative', zIndex: 20 }}
          onClick={() => setOpen(true)}
        >
          <TbMenu2 className="w-6 h-6" />
        </button>

        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full text-center px-4 md:px-12">
          <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 truncate">
            {pageTitle}
          </h1>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 ml-auto">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden md:block">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-64 px-4 py-2 pl-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
              />
              <TbSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </form>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors relative touch-target"
              aria-label="Notifications"
            >
              <TbBell className="w-5 h-5 sm:w-6 sm:h-6" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-screen max-w-sm sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Notifications</h3>
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="sm:hidden p-1 hover:bg-gray-100 rounded"
                    aria-label="Close"
                  >
                    <TbBell className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <p className="text-sm">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 focus:outline-none"
            >
              <div className="hidden md:block text-right">
                <span className="text-sm font-medium text-gray-900">{userName}</span>
                {user?.branch && (
                  <p className="text-xs text-gray-500">{user.branch.name || user.branch.branch_name}</p>
                )}
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-gray-200">
                <img
                  src={AdminImage}
                  alt={userName}
                  className="w-full h-full object-cover"
                />
              </div>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                >
                  <FiUser className="w-4 h-4" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => navigate("/settings")}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                >
                  <FiSettings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                >
                  <FiLogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <SideDrawer
        name={userName}
        profilePic={AdminImage}
        open={open}
        setOpen={setOpen}
        className="md:hidden"
      />
    </div>
  );
};

export default TopBar;
